// The class's assignments on the class page: each one's rules and how the students are
// getting on, with Assign Practice, Publish (for drafts) and Archive for its teachers.
import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { createLogger } from '../../utils/logger';
import ConfirmationModal from '../shared/ConfirmationModal';
import AssignmentRulesSummary from '../assignments/AssignmentRulesSummary';
import {
  ClassAssignment,
  archiveClassAssignment,
  fetchClassAssignments,
  publishClassAssignment,
} from '../../services/classService';
import AssignPracticeWizard from './AssignPracticeWizard';
import {
  cardStyle,
  dangerLinkStyle,
  errorMessage,
  errorStyle,
  hintStyle,
  linkButtonStyle,
  noticeStyle,
  primaryButtonStyle,
  sectionHeadingStyle,
  statusBadgeStyle,
} from './classesStyles';

const logger = createLogger('ClassAssignmentsSection');

interface Props {
  classId: string;
  /** Teachers and school admins of this class; grantees only look. */
  canAssign: boolean;
}

const ClassAssignmentsSection: React.FC<Props> = ({ classId, canAssign }) => {
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState<ClassAssignment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [archiving, setArchiving] = useState<ClassAssignment | null>(null);

  const load = useCallback(async () => {
    try {
      setAssignments(await fetchClassAssignments(classId));
    } catch (err) {
      logger.error('Could not load class assignments', err);
      setError(t('classAssignments.couldNotLoad'));
    }
  }, [classId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (work: () => Promise<unknown>, success: string, fallback: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await work();
      setNotice(success);
      await load();
    } catch (err) {
      logger.error(fallback, err);
      setError(errorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={cardStyle} id="tour-class-assignments">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ ...sectionHeadingStyle, margin: 0 }}>{t('classAssignments.title')}</h2>
        {canAssign && (
          <button type="button" style={primaryButtonStyle} onClick={() => setAssigning(true)} disabled={busy}>
            <ClipboardPlus size={16} /> {t('classAssignments.assignPractice')}
          </button>
        )}
      </div>
      <div style={{ marginTop: '0.75rem' }}>
        {error && <div style={errorStyle} role="alert">{error}</div>}
        {notice && <div style={noticeStyle} role="status">{notice}</div>}
        {assignments !== null && assignments.length === 0 && <p style={hintStyle}>{t('classAssignments.none')}</p>}
        {assignments?.map(a => (
          <details key={a.id} style={{ borderTop: '1px solid #e2e8f0', padding: '0.75rem 0' }}>
            <summary style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '0.95rem' }}>{a.title}</strong>
              <span style={statusBadgeStyle(a.assignment_status === 'published')}>
                {t(`classAssignments.statuses.${a.assignment_status}`)}
              </span>
              <span style={{ ...hintStyle, margin: 0 }}>
                {t('classAssignments.progressLine', {
                  completed: a.progress.completed,
                  inProgress: a.progress.in_progress,
                  students: a.progress.students,
                })}
              </span>
            </summary>
            <div style={{ padding: '0.75rem 0 0 1rem' }}>
              {a.description && <p style={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>{a.description}</p>}
              <p style={{ ...hintStyle, marginTop: 0 }}>{a.bundle_names.join(', ')}</p>
              <AssignmentRulesSummary rules={a.rules} dueDate={a.due_date} />
              {canAssign && (
                <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                  {a.assignment_status === 'draft' && (
                    <button
                      type="button"
                      style={linkButtonStyle}
                      disabled={busy}
                      onClick={() =>
                        run(
                          () => publishClassAssignment(classId, a.id),
                          t('classAssignments.published'),
                          t('classAssignments.couldNotSave')
                        )
                      }
                    >
                      {t('classAssignments.publish')}
                    </button>
                  )}
                  <button type="button" style={dangerLinkStyle} disabled={busy} onClick={() => setArchiving(a)}>
                    {t('classes.archive')}
                  </button>
                </div>
              )}
            </div>
          </details>
        ))}
      </div>

      {assigning && (
        <AssignPracticeWizard
          classId={classId}
          onClose={() => setAssigning(false)}
          onDone={() => {
            setAssigning(false);
            setNotice(t('classAssignments.saved'));
            load();
          }}
        />
      )}

      <ConfirmationModal
        isOpen={archiving !== null}
        title={t('classAssignments.archiveTitle')}
        message={archiving ? t('classAssignments.archiveMessage', { title: archiving.title }) : ''}
        confirmText={t('classes.archive')}
        loading={busy}
        onCancel={() => setArchiving(null)}
        onConfirm={() => {
          const target = archiving;
          setArchiving(null);
          if (target) {
            run(
              () => archiveClassAssignment(classId, target.id),
              t('classAssignments.archived'),
              t('classAssignments.couldNotSave')
            );
          }
        }}
      />
    </section>
  );
};

export default ClassAssignmentsSection;
